import type { ColumnMetadata, RelatedColumnDescriptor } from '../types';
import { EMPLOYEE_REVIEW_RATING_FIELDS } from '../../colleagueRating/constants';

export const normalizeGridKey = (key?: string | null) =>
  key
    ?.trim()
    .replace(/^\{|\}$/g, '')
    .toLowerCase() ?? '';

/**
 * Known paired date fields for worklog inline editing.
 * When one field in a pair is filled, the other uses it as the default value
 * in the date picker. This is a fallback for grids where the backend does not
 * return an explicit pairedDateField in column metadata.
 */
const PAIRED_DATE_FIELDS: Record<string, string> = {
  LogStart: 'LogEnd',
  LogEnd: 'LogStart',
};

/**
 * Parse column metadata from various input formats
 * Handles both string JSON and object arrays from backend
 *
 * @param columnsData - Raw column data from backend (can be string JSON or array)
 * @returns Parsed array of ColumnMetadata objects
 */
export function parseColumnMetadata(columnsData: unknown): ColumnMetadata[] {
  if (!columnsData) return [];

  try {
    // Handle string JSON (backend might return stringified JSON)
    let columns = columnsData;
    if (typeof columnsData === 'string') {
      columns = JSON.parse(columnsData);
    }

    if (Array.isArray(columns)) {
      const usesEmployeeReviewRatingEditor = columns.some(
        (col: Record<string, unknown>) =>
          col.nameJsMethod === 'ZAssets.Fields.MethodShowEmployeeReviewFeedbackContext'
      );
      return columns.map((col: Record<string, unknown>) => {
        const field = String(col.dataField ?? col.field ?? col.Field ?? '');
        const title = String(col.caption ?? col.title ?? col.Title ?? field);
        const configuredPairedDateField = col.pairedDateField ?? col.PairedDateField;
        const pairedDateField =
          typeof configuredPairedDateField === 'string' && configuredPairedDateField
            ? configuredPairedDateField
            : PAIRED_DATE_FIELDS[field];
        const type = col.dataType ?? col.type ?? col.Type;
        const rawWidth = col.width ?? col.minWidth ?? col.Width;
        const rawIsUrl = col.isUrl ?? col.IsUrl;
        const isUrl = typeof rawIsUrl === 'boolean' ? rawIsUrl : undefined;
        const sortingBlockedExplicit = col.allowSorting === false || col.AllowSorting === false;
        const totalSummariesValue = col.totalSummaries ?? col.TotalSummaries;
        const totalSummaries =
          typeof totalSummariesValue === 'string' ? totalSummariesValue : undefined;

        const groupIndexValue = col.groupIndex ?? col.GroupIndex;
        const groupIndex = typeof groupIndexValue === 'number' ? groupIndexValue : undefined;
        const rawGroupSummaries = col.groupSummaries ?? col.GroupSummaries;
        const summaries =
          rawGroupSummaries && typeof rawGroupSummaries === 'object'
            ? (rawGroupSummaries as Record<string, unknown>)
            : undefined;
        const groupSummaries = summaries
          ? {
              summaryType: summaries.summaryType ?? summaries.SummaryType ?? null,
              showInGroupFooter: summaries.showInGroupFooter ?? summaries.ShowInGroupFooter ?? null,
              alignByColumn: summaries.alignByColumn ?? summaries.AlignByColumn ?? null,
            }
          : undefined;
        const canEdit = col.canEdit ?? col.CanEdit;
        const canView = col.canView ?? col.CanView;
        const isVisible = col.visible ?? col.Visible;
        const allowEditing = col.allowEditing ?? col.AllowEditing;
        const editable =
          canEdit === false
            ? false
            : canEdit === true
              ? true
              : allowEditing !== false && col.editable !== false;

        return {
          field,
          title,
          type,
          dataType: col.dataType,
          allowSorting: !sortingBlockedExplicit,
          // Keep the unit so percentage widths can be applied as CSS values.
          width: rawWidth != null && rawWidth !== '' ? String(rawWidth) : undefined,
          visible: canView !== false && isVisible !== false && !col.hiddenForMetaData,
          editable,
          allowEditing,
          format: col.format || col.Format,
          dataField: col.dataField,
          pairedDateField,
          caption: col.caption,
          defaultValue: col.defaultValue,
          defaultFilters: col.defaultFilters ?? col.DefaultFilters,
          validationRules: col.validationRules,
          isUrl,
          editor:
            usesEmployeeReviewRatingEditor && EMPLOYEE_REVIEW_RATING_FIELDS.has(field.toLowerCase())
              ? 'ratingselect'
              : col.editor,
          key: col.key,
          nameJsMethod: (col.nameJsMethod ?? null) as string | null,
          jsMethodOptions: (col.jsMethodOptions ?? null) as string | null,
          canView,
          canCreate: col.canCreate ?? col.CanCreate,
          canEdit,
          additionalData: col.additionalData,
          cellTemplateFunction: col.cellTemplateFunction ?? col.CellTemplateFunction ?? null,
          cellTemplateFunctionParams:
            col.cellTemplateFunctionParams ?? col.CellTemplateFunctionParams ?? null,
          hasColor: col.hasColor ?? col.HasColor ?? false,
          colorColumnName: col.colorColumnName ?? col.ColorColumnName ?? null,
          totalSummaries,
          groupIndex,
          groupSummaries,
        } as ColumnMetadata;
      });
    }

    return [];
  } catch (err) {
    console.error('Error parsing columns:', err);
    return [];
  }
}

/**
 * Convert lookup maps object to Map for use in DataGrid
 * Backend returns lookupMaps as a plain object, but DataGrid expects Map
 *
 * @param lookupMaps - Plain object with lookup data
 * @returns Map with the same data structure
 */
export function convertLookupMapsToMap(
  lookupMaps: Record<string, Record<string, string>> | undefined
): Map<string, Record<string, string>> | undefined {
  if (!lookupMaps) return undefined;

  const map = new Map<string, Record<string, string>>();
  Object.entries(lookupMaps).forEach(([key, value]) => {
    map.set(key, value);
  });

  return map;
}

/**
 * Extract field keys from column metadata
 * Used for specifying which fields to fetch from backend
 *
 * @param columns - Array of column metadata
 * @returns Array of field key strings
 */
export function extractFieldKeys(columns: ColumnMetadata[] | undefined): string[] {
  if (!columns || !Array.isArray(columns)) return [];

  return columns.map((col) => col.key).filter((key): key is string => !!key);
}

type RawItem = {
  Id?: string | number;
  DeletedAt?: string | null;
  Fields?: { Name: string; StringValue: string }[];
  [key: string]: unknown;
};

/**
 * Flatten raw data from backend API response
 * Converts nested structure with Fields array to flat object
 *
 * @param rawData - Raw data array from backend with nested Fields
 * @returns Flattened array of objects
 */
export function flattenRawData(rawData: unknown[]): Record<string, unknown>[] {
  if (!Array.isArray(rawData)) return [];

  const result: Record<string, unknown>[] = new Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    const item = rawData[i] as RawItem;

    if (Array.isArray(item.Fields)) {
      // MapEntityList format: { Id, DeletedAt, Fields: [{ Name, StringValue }] }
      const flatItem: Record<string, unknown> = {
        Id: item.Id,
        DeletedAt: item.DeletedAt,
      };
      for (let j = 0; j < item.Fields.length; j++) {
        const field = item.Fields[j];
        flatItem[field.Name] = field.StringValue;
      }
      result[i] = flatItem;
    } else {
      // MappedListFromDynamic format: flat object with property names as keys
      result[i] = item;
    }
  }

  return result;
}

/**
 * Parse and filter related columns from additionalData.relatedColumns JSON string.
 * Filters out columns with width or minWidth of 0 (hidden columns).
 */
export function parseRelatedColumns(
  additionalData?: ColumnMetadata['additionalData']
): RelatedColumnDescriptor[] {
  try {
    const parsed: RelatedColumnDescriptor[] = JSON.parse(additionalData?.relatedColumns || '[]');
    return parsed.filter((col) => {
      const widthValue = col.width || col.minWidth;
      if (!widthValue) return true;
      return parseInt(widthValue, 10) !== 0;
    });
  } catch {
    return [];
  }
}

/**
 * Recursively converts object keys from PascalCase to camelCase
 *
 * @param obj - Object to convert (can be nested object, array, or primitive)
 * @returns Object with camelCase keys
 */
export function toCamelCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const key in obj as object) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        result[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
      }
    }
    return result;
  }

  return obj;
}
